/**
 * "Office action" (1.5.17 §3b) — what a tenant asks the office for on the phone, each one tap and done, each logged
 * with who, when and "by phone". The administrator and the co-administrator only: "we're marking it for the person,
 * not for the crew" — never the scheduler, the crew, a tech or a viewer.
 *
 *   Tenant cancelled today (by phone)   the ordinary Queue 1 cancel with the tenant as initiator; no email by that
 *                                       press — "Send the tenant a confirmation" is a second press on the same sheet.
 *   Permission to enter                 by email (the tenant's own link, 1.4.0), or recorded by phone: staff reads the
 *                                       terms below aloud, the tenant agrees, staff types both names and ticks "Read
 *                                       to the tenant by phone, the tenant agreed" — a one-page PDF in the tenant's
 *                                       folder, a copy to Cynthia, and the crew's Arrived sheet says it is on file.
 *
 * The terms are plain words, seeded here in English and Spanish and edited under Settings → Forms →
 * "Permission to enter" (`_app/settings/permission-terms.json`). No dependencies beyond zod, so the app and the API
 * both use it.
 */
import { z } from 'zod';

/** Who has Office action. */
export const OFFICE_ROLES = ['admin', 'coadmin'] as const;

/** Plain words, read aloud by staff: a title and one short paragraph per thing the tenant agrees to. */
export interface PermissionTerms {
  title: string;
  paragraphs: string[];
}

/** The terms in both languages — what Settings → Forms edits and the store keeps. */
export interface PermissionTermsText {
  en: PermissionTerms;
  es: PermissionTerms;
}

export interface PermissionTermsResponse {
  /** Changes when the wording changes (a hash of both languages). */
  version: string;
  en: PermissionTerms;
  es: PermissionTerms;
  tenantLang: 'en' | 'es';
  tenantName: string;
  unit: string;
  dayKey: string;
  /** False → the "email the request" way is not offered (no address, or the tenant asked for no email). */
  tenantHasEmail: boolean;
  officePhone: string | null;
}

export interface PermissionPhoneRequest {
  appointmentId: string;
  /** The tenant's name, typed by staff. */
  tenantName: string;
  /** The staff member's own name, typed. */
  staffName: string;
  /** The language the terms were read in. */
  lang: 'en' | 'es';
  /** The tick: "Read to the tenant by phone, the tenant agreed". */
  readAndAgreed: true;
}

export interface PermissionPhoneResponse {
  status: 'granted';
  how: 'phone';
  at: string;
  pdfPath: string;
  copyCommunicationId: string | null;
  copyScheduledAt: string | null;
  /** Why no copy was queued (or who it went to instead), in words, when it did not go to Cynthia. */
  copyNote: string | null;
}

export interface CancelConfirmationResponse {
  communicationId: string;
  scheduledAt: string;
}

/** What the tick says — on the screen (worker D's i18n carries the Spanish) and, in English, on the PDF. */
export const READ_AND_AGREED_WORDS = 'Read to the tenant by phone, the tenant agreed';
/** The document's name: its title, its footer, its file. */
export const PERMISSION_PDF_TITLE = 'Permission to enter';

/** The seeded terms: what the crew will do, that nobody else enters, this visit only, photos for the record. */
export const PERMISSION_TERMS_DEFAULT: PermissionTermsText = {
  en: {
    title: 'Permission to enter your apartment for this visit',
    paragraphs: [
      'Our housekeeping crew will clean your apartment the way they do on a normal housekeeping visit.',
      'Only the housekeeping crew will go in. Nobody else enters your apartment.',
      'This permission is for this one visit only. It is never standing permission; we will ask you again another time.',
      'The crew takes photos before and after the cleaning, for the record.',
    ],
  },
  es: {
    title: 'Permiso para entrar a su apartamento en esta visita',
    paragraphs: [
      'Nuestro equipo de limpieza limpiará su apartamento como en una visita de limpieza normal.',
      'Solo entrará el equipo de limpieza. Nadie más entra a su apartamento.',
      'Este permiso es solo para esta visita. Nunca es un permiso permanente; se lo pediremos de nuevo en otra ocasión.',
      'El equipo toma fotos antes y después de la limpieza, para el registro.',
    ],
  },
};

export const PERMISSION_TERMS_FOLDER = '_app/settings';
export const PERMISSION_TERMS_FILE = 'permission-terms.json';
export const MAX_TERMS_PARAGRAPHS = 8;

const Terms = z.object({
  title: z.string().trim().min(2, 'The terms need a title.').max(160),
  paragraphs: z.array(z.string().trim().min(1).max(600)).min(1, 'The terms need at least one line.').max(MAX_TERMS_PARAGRAPHS),
});
/** PUT /api/manage/permission-terms — both languages, every time. */
export const PermissionTermsSaveSchema = z.object({ en: Terms, es: Terms });

/** The editor's textarea: one paragraph per line. */
export const paragraphsOf = (text: string): string[] => text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);

/** The words exactly as read, in one language — what is printed on the PDF and what its hash is of. */
export function termsText(t: PermissionTerms): string {
  return [t.title, ...t.paragraphs].join('\n\n');
}

/** "If you would rather, call the office at …" — added to the emailed ask when OFFICE_PHONE is set. */
export const OFFICE_PHONE_LINE = {
  en: 'If you would rather, call the office at {phone}.',
  es: 'Si lo prefiere, llame a la oficina al {phone}.',
} as const;

/** A wording that already offers the call (the Director added it to the template) is left alone. */
export const OFFERS_THE_CALL = /if you would rather|si lo prefiere/i;
